import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { IonFab, IonFabButton, IonFabList, IonIcon } from '@ionic/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { closeOutline, shieldCheckmarkOutline } from 'ionicons/icons';
import { filter } from 'rxjs/operators';
import { ADMIN_PAGES } from '../../admin-pages';
import { AuthService } from '../../providers/auth.service';

/**
 * A floating "Admin" button, bottom-right on every page, in the style of the
 * Ionic conference app's share button: tap it and a column of small round
 * buttons fans out above it, one per admin page (Inventory, Users, Orders,
 * Requests, Settings); tap it again (now an X) to fold them away.
 *
 * Only rendered for an admin account (`AuthService.isAdmin`) — the pages and
 * the API enforce that independently, this just keeps it from showing to
 * anyone else. Hidden where a floating button only gets in the way: the
 * sign-in/up pages and the printable packing slips.
 */
@Component({
  selector: 'app-admin-fab',
  templateUrl: './admin-fab.html',
  styleUrl: './admin-fab.scss',
  imports: [IonFab, IonFabButton, IonFabList, IonIcon, TranslatePipe],
})
export class AdminFabComponent {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  protected readonly pages = ADMIN_PAGES;
  protected readonly icons = {
    admin: shieldCheckmarkOutline,
    close: closeOutline,
  };
  private readonly url = signal(this.router.url);

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(e => this.url.set(e.urlAfterRedirects));
  }

  /** True while on any admin page — tints the main button. */
  protected readonly inAdmin = computed(() => this.url().startsWith('/admin'));

  protected readonly visible = computed(() => {
    if (!this.auth.isAdmin()) {
      return false;
    }
    const path = this.url().split(/[?#]/)[0];
    return !/^\/(login|signup)(\/|$)/.test(path) && !path.endsWith('/print');
  });

  /** True while on the admin page `url` (exact match: `/admin` is Inventory). */
  protected isCurrent(url: string): boolean {
    return this.url().split(/[?#]/)[0] === url;
  }

  /** Ionic's own fab-list buttons toggle the fab on click, which folds the list
   * away — closing it here as well would just have it toggled open again. */
  protected go(url: string): void {
    void this.router.navigateByUrl(url);
  }
}
